import React from 'react';
import { EquipeLite } from '../../../../services/faseGrupoPartidasService';
import type { LinhaClassificacaoFront } from '../../../../services/classificacaoService';

interface TeamsTableProps {
  equipes: EquipeLite[];
  linhasClassificacao: LinhaClassificacaoFront[];
}

/** Linha renderizável: uma equipe membro do grupo, com a classificação sobreposta (se houver). */
interface LinhaEquipe {
  equipeId: string;
  equipe: EquipeLite | undefined;
  classificacao: LinhaClassificacaoFront | null;
}

const TeamsTable: React.FC<TeamsTableProps> = ({
  equipes,
  linhasClassificacao,
}) => {
  // Lookup map para encontrar equipe pelo id
  const equipesMap = new Map<string, EquipeLite>(
    equipes.map((e) => [e.id, e]),
  );

  // A BASE da lista são os membros do grupo (equipes), não a classificação.
  // A classificação só conta partidas FINALIZADAS, então um grupo recém-gerado
  // tem classificação vazia — as equipes membro precisam aparecer mesmo assim.
  // Exceção (modo por-atleta/rodízio): quando não há Equipe-de-1 carregada mas
  // já existem linhas de classificação (equipeId=atletaId, rotulado via
  // nomeCompetidor), a tabela é dirigida pela classificação mesmo com
  // `equipes` vazio — só mostra o aviso quando as DUAS listas estão vazias.
  if (equipes.length === 0 && linhasClassificacao.length === 0) {
    return (
      <div className="alert alert-info">
        <p className="mb-0">Nenhuma equipe neste grupo.</p>
      </div>
    );
  }

  // Ordem: primeiro as linhas de classificação (ordem do MotorClassificacao,
  // que já aplica os critérios de desempate — não reordenamos no cliente),
  // depois as equipes membro que ainda não possuem linha de classificação.
  const idsClassificados = new Set(linhasClassificacao.map((l) => l.equipeId));
  const linhas: LinhaEquipe[] = [
    ...linhasClassificacao.map((classificacao) => ({
      equipeId: classificacao.equipeId,
      equipe: equipesMap.get(classificacao.equipeId),
      classificacao,
    })),
    ...equipes
      .filter(
        (e) =>
          !idsClassificados.has(e.id) &&
          !(e.atletas ?? []).some((a) => idsClassificados.has(a.id)),
      )
      .map((equipe) => ({ equipeId: equipe.id, equipe, classificacao: null })),
  ];

  return (
    <div className="table-responsive">
      <table className="table table-striped table-hover">
        <thead className="table-light">
          <tr>
            <th style={{ width: '60px' }}>Pos.</th>
            <th>Equipe</th>
            <th style={{ width: '80px' }}>V</th>
            <th style={{ width: '80px' }}>D</th>
            <th style={{ width: '100px' }}>Sets</th>
            <th style={{ width: '100px' }}>Games</th>
          </tr>
        </thead>
        <tbody>
          {linhas.map(({ equipeId, equipe, classificacao }) => {
            // Equipes SOLO costumam ter nome vazio (""): usa o nome do(s) atleta(s).
            // Modo por-atleta (rodízio): classificacao.equipeId é na verdade um
            // atletaId e não casa com nenhuma Equipe-de-1 do grupo — o rótulo
            // deve preferir nomeCompetidor (entregue pelo backend) nesse caso.
            const nomesAtletas = (equipe?.atletas ?? [])
              .map((a) => a.usuario?.nome || '')
              .filter((nome) => nome.length > 0);
            const rotulo =
              classificacao?.nomeCompetidor ||
              (nomesAtletas.length > 0 ? nomesAtletas.join(', ') : equipe?.nome || '—');

            return (
              <tr key={equipeId} data-testid={`classificacao-row-${equipeId}`}>
                <td className="fw-bold">{classificacao ? `${classificacao.posicao}º` : '—'}</td>
                <td>
                  <div className="d-flex align-items-center">
                    {equipe?.atletas && equipe.atletas.length > 0 && (
                      <div className="d-flex me-2">
                        {equipe.atletas.slice(0, 2).map((atleta) => (
                          <img
                            key={atleta.id}
                            src={
                              atleta.usuario?.imagemUrl ||
                              '/assets/images/user.png'
                            }
                            alt={atleta.usuario?.nome || 'Atleta'}
                            className="rounded-circle"
                            style={{
                              width: '32px',
                              height: '32px',
                              objectFit: 'cover',
                              marginLeft: '-8px',
                              border: '2px solid #fff',
                            }}
                            title={atleta.usuario?.nome || ''}
                          />
                        ))}
                      </div>
                    )}
                    <div>
                      <small className="fw-semibold">{rotulo}</small>
                    </div>
                  </div>
                </td>
                <td className="text-success fw-bold">
                  {classificacao ? classificacao.vitorias : '—'}
                </td>
                <td className="text-danger fw-bold">
                  {classificacao ? classificacao.derrotas : '—'}
                </td>
                <td>
                  {classificacao
                    ? `${classificacao.setsPro}/${classificacao.setsContra}`
                    : '—'}
                </td>
                <td>
                  {classificacao
                    ? `${classificacao.gamesPro}/${classificacao.gamesContra}`
                    : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default TeamsTable;
